import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { NumericFormat } from 'react-number-format'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, Checkbox, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { selectOrderItems } from '@/app/services/orderItems'
import { selectSelectedItems } from '@/app/slices/orderItemsSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'

const classes = {
    formControl_medium: {
        width: '140px',
    },
    formControl_small: {
        width: '100px',
    },
}

const FoldingBulkEdit = ({
    itemMaxLength,
    itemNumberOfFolds,
    itemShowFolding,
    itemSource,
    itemUnit,
    setItemMaxLength,
    setItemNumberOfFolds,
    setItemSource,
    setUserMadeFoldingChanges,
}) => {
    const { t } = useTranslation()
    const { orderId } = useParams()

    const organisationId = useSelector(selectOrganisationId)
    const orderItems = useSelector((state) => selectOrderItems(state, { organisationId, orderId }))
    const selectedItemsArray = useSelector(selectSelectedItems)

    const orderItemsFromSelection = useMemo(
        () => orderItems.filter((item) => selectedItemsArray.includes(item.id)),
        [orderItems, selectedItemsArray]
    )

    const handleSourceChange = async (event) => {
        const value = event?.target?.value
        if (Boolean(value) && itemSource !== value) {
            setItemSource(value)
            setUserMadeFoldingChanges(true)
        }
    }

    const handleMaxLengthChange = (values) => {
        const { floatValue } = values
        setItemMaxLength(floatValue)
        setUserMadeFoldingChanges(true)
    }

    const handleNumberOfFoldsChange = (values) => {
        const { floatValue } = values
        setItemNumberOfFolds(floatValue)
        setUserMadeFoldingChanges(true)
    }

    const allItemsHaveSameProperty = (array, property) => {
        return array.every((element) => element[property] === array[0][property])
    }

    useEffect(() => {
        if (!orderItemsFromSelection.length) return

        if (allItemsHaveSameProperty(orderItemsFromSelection, 'foldingSource')) {
            setItemSource(orderItemsFromSelection[0].foldingSource)
        }

        if (allItemsHaveSameProperty(orderItemsFromSelection, 'numberOfFolds')) {
            setItemNumberOfFolds(orderItemsFromSelection[0].numberOfFolds || 1)
        }

        if (allItemsHaveSameProperty(orderItemsFromSelection, 'maxFoldLength')) {
            setItemMaxLength(orderItemsFromSelection[0].maxFoldLength)
        }
    }, [orderItemsFromSelection])

    return (
        <Box
            sx={{
                alignItems: 'center',
                display: 'flex',
                flex: '1',
                justifyContent: 'space-between',
                width: '100%',
            }}
        >
            <Box display={'flex'}>
                <Checkbox
                    checked={itemShowFolding === 'all' ? true : false}
                    color="secondary"
                    data-testid="bulk-edit-secondary-process-option-checkbox"
                    indeterminate={itemShowFolding === 'some' ? true : false}
                    size="small"
                    sx={{ p: 0, mr: 1 }}
                />
                <Typography
                    data-testid="bulk-edit-folding-section-title"
                    variant="body2"
                >
                    {t('Folding')}
                </Typography>
            </Box>

            <Box
                display="flex"
                gap={1.5}
            >
                <FormControl
                    color="primary"
                    size="small"
                    sx={classes.formControl_medium}
                >
                    <InputLabel>{t('Input mode')}</InputLabel>
                    <Select
                        data-testid="order-item-source"
                        id="select-itemSource"
                        label={t('Input mode')}
                        labelId="select-itemSource-label"
                        SelectDisplayProps={{
                            style: { fontSize: '14px', paddingBlock: '5px', paddingInline: '12px' },
                        }}
                        value={itemSource}
                        onBlur={handleSourceChange}
                        onChange={handleSourceChange}
                    >
                        <MenuItem
                            key={'Drawing'}
                            value={'Drawing'}
                        >
                            {t('From drawing')}
                        </MenuItem>
                        <MenuItem
                            key={'Manual'}
                            value={'Manual'}
                        >
                            {t('Manual input')}
                        </MenuItem>
                    </Select>
                </FormControl>

                <NumericFormat
                    allowNegative={false}
                    customInput={TextField}
                    disabled={itemSource === 'Drawing' || !itemUnit}
                    label={t('Longest fold')}
                    placeholder={itemUnit}
                    size="small"
                    slotProps={{
                        inputLabel: {
                            shrink: true,
                        },
                        input: {
                            style: { fontSize: '14px' },
                        },
                        htmlInput: {
                            style: { textAlign: 'right', paddingBlock: '6px', paddingInline: '12px' },
                            'data-testid': 'order-item-longest-fold',
                        },
                    }}
                    suffix={` ${itemUnit}`}
                    sx={classes.formControl_small}
                    value={itemSource === 'Drawing' ? '' : itemMaxLength}
                    variant="outlined"
                    thousandSeparator
                    onValueChange={handleMaxLengthChange}
                />

                <NumericFormat
                    allowNegative={false}
                    customInput={TextField}
                    disabled={itemSource === 'Drawing'}
                    label={t('N° of folds')}
                    size="small"
                    slotProps={{
                        inputLabel: {
                            shrink: true,
                        },
                        input: {
                            style: { fontSize: '14px' },
                        },
                        htmlInput: {
                            style: { textAlign: 'right', paddingBlock: '6px', paddingInline: '12px' },
                            'data-testid': 'order-item-number-of-folds',
                        },
                    }}
                    sx={classes.formControl_small}
                    value={itemSource === 'Drawing' ? '' : itemNumberOfFolds}
                    variant="outlined"
                    fullWidth
                    thousandSeparator
                    onValueChange={handleNumberOfFoldsChange}
                />
            </Box>
        </Box>
    )
}

FoldingBulkEdit.propTypes = {
    itemMaxLength: PropTypes.number,
    itemNumberOfFolds: PropTypes.number,
    itemShowFolding: PropTypes.bool,
    itemSource: PropTypes.string,
    itemUnit: PropTypes.string,
    setItemMaxLength: PropTypes.func,
    setItemNumberOfFolds: PropTypes.func,
    setItemSource: PropTypes.func,
    setUserMadeFoldingChanges: PropTypes.func,
}

export default FoldingBulkEdit
